import React, { useState, useMemo } from 'react';
import cn from 'classnames';
import { useFormContext } from 'react-hook-form';
import DropdownMenuIcon from '../DropdownMenuIcon';
import DATA_TEST_IDS from 'chameleon-src/components/assign/AssignManageForm/utils/dataTestIds';
import { FIELD_NAMES } from '../../BasicSettings';
import { includes, filter, sortBy } from 'lodash';
import { hasDisabledStudentsInSelectedClass } from '../utils';
import {
  getStudentsAssignmentSettingPostfix,
  getActiveClassSelection,
} from './util';

const StudentCheckbox = ({
  student,
  isSelected,
  onChange,
  isDisabled,
  sectionId,
  otherActiveSettings,
  getValues,
}) => {
  const studentAssignmentSettingPostfix = getStudentsAssignmentSettingPostfix(
    sectionId,
    student.id,
    otherActiveSettings,
    getValues,
  );
  const shouldApplyItalics = !!studentAssignmentSettingPostfix;

  return (
    <label
      key={student.id}
      className={cn('px-4 py-1 flex items-center', {
        italic: shouldApplyItalics,
      })}
    >
      {!isDisabled ? (
        <>
          <input
            type="checkbox"
            value={student.id}
            onChange={onChange}
            checked={isSelected}
            disabled={isDisabled}
            data-test-id={DATA_TEST_IDS.STUDENT_INPUT_CHECKBOX}
            className="mr-2"
          />
          {`${student.firstName} ${student.lastName}`}
          {studentAssignmentSettingPostfix}
        </>
      ) : (
        <>
          <span className="inline-block w-5 h-5 mr-3"></span>
          <span className="opacity-50 text-gray-600 flex-1 ml-1">
            {`${student.firstName} ${student.lastName}`}
          </span>
        </>
      )}
    </label>
  );
};

const ClassItemDropdown = ({
  cls,
  handleCheckboxChange,
  settingId,
  disabledStudents,
  otherActiveSettings,
  getValues,
}) => {
  const [expanded, setExpanded] = useState(false);
  const { watch } = useFormContext();
  const selectedClasses = watch(`${settingId}.${FIELD_NAMES.CLASS_DROPDOWN}`);

  const toggleClass = (e) => {
    if (cls.students.length > 0 && e.target.value === undefined) {
      setExpanded((exp) => !exp);
    }
  };

  const isStudentDisabled = (studentId) =>
    disabledStudents.some(
      (ds) => ds.studentId === studentId && ds.sectionId === cls.id,
    );

  const allStudentsSelected = useMemo(() => {
    if (cls.students.length === 0) {
      // Check if the class entry exists to determine if it's selected
      return selectedClasses[cls.id] !== undefined;
    } else {
      // Filter out disabled students before checking if all are selected
      const enabledStudents = cls.students.filter(
        (student) => !isStudentDisabled(student.id),
      );
      return enabledStudents.every((student) =>
        includes(selectedClasses[cls.id]?.student_ids, student.id),
      );
    }
  }, [cls.students, selectedClasses, cls.id, isStudentDisabled]);

  const sortedStudents = sortBy(cls.students, (student) =>
    student.lastName ? student.lastName.toLowerCase() : '',
  );

  const selectedStudentCount = useMemo(
    () =>
      filter(cls.students, (student) =>
        includes(selectedClasses[cls.id]?.student_ids, student.id),
      ).length,
    [cls.students, selectedClasses, cls.id],
  );

  const studentCountLabel =
    selectedStudentCount === 1
      ? `${selectedStudentCount} Student`
      : `${selectedStudentCount} Students`;

  const hasDisabledStudents = hasDisabledStudentsInSelectedClass(
    cls,
    disabledStudents,
  );

  const { isFullClassSelectedElsewhere = false, settingNumber = 0 } =
    getActiveClassSelection(
      cls,
      otherActiveSettings,
      disabledStudents,
      getValues,
    );
  const sectionName = isFullClassSelectedElsewhere
    ? `${cls.name} (Settings ${settingNumber})`
    : cls.name;

  return (
    <div key={cls.id} className="border-b border-gray-200 last:border-b-0">
      <div className="flex justify-between items-center px-4 py-2">
        <div
          className="flex items-center flex-grow cursor-pointer"
          data-test-id={DATA_TEST_IDS.ASSIGNABLE_SECTIONS}
          onClick={toggleClass}
        >
          <input
            type="checkbox"
            value={cls.id}
            onChange={() => handleCheckboxChange(cls.id)}
            checked={allStudentsSelected}
            className="mr-2"
          />
          <span
            role="button"
            tabIndex={0}
            aria-label={`Toggle ${cls.name}`}
            className={cn('cursor-pointer', {
              italic: isFullClassSelectedElsewhere,
            })}
            data-test-id={`${DATA_TEST_IDS.CLASSNAME_DROPDOWN_TOGGLE}-${cls.id}`}
          >
            {sectionName}
          </span>
          {!allStudentsSelected && selectedStudentCount > 0
            ? ` (${studentCountLabel})`
            : hasDisabledStudents && allStudentsSelected
              ? ' (Everyone Else)'
              : ''}
        </div>
        {cls.students.length > 0 && (
          <DropdownMenuIcon
            onClick={toggleClass}
            className={`flex items-center cursor-pointer ${expanded ? 'transform rotate-180' : ''}`}
            accessibility={{ ariaExpanded: expanded }}
            data-test-id={`${DATA_TEST_IDS.CLASSES_DROPDOWN_TOGGLE}-${cls.id}`}
            iconId="selectbox_arrow_down"
            iconClassName="text-gray-600"
          />
        )}
      </div>
      {expanded && (
        <div className="pl-6">
          {sortedStudents.map((student) => (
            <StudentCheckbox
              data-test-id={`${DATA_TEST_IDS.ASSIGN_STUDENT}-${student.id}`}
              key={student.id}
              student={student}
              isSelected={includes(
                selectedClasses[cls.id]?.student_ids,
                student.id,
              )}
              onChange={(e) =>
                handleCheckboxChange(cls.id, student.id, e.target.checked)
              }
              isDisabled={isStudentDisabled(student.id)}
              otherActiveSettings={otherActiveSettings}
              sectionId={cls.id}
              getValues={getValues}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ClassItemDropdown;
